import { getStatusOptions } from '@/components/deal/status/deal-status-selector';
import type { DealListItem } from '@/models/types';
import {
  type DEALS_GROUPED_BY_STATUS,
  // type DealListItem,
  type Status,
  Statuses,
} from '@/models/types';
import { useMemo } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import DealCard from './deal-card';

const STATUS_OPTIONS_MAP = getStatusOptions({ className: 'size-4' });

const VirtualizedDealBoard = ({
  dealsData,
  filterCallback,
  enableScope,
  shouldApplyFilteredDealsIds,
  findInViewFilterCallback,
}: {
  dealsData: DEALS_GROUPED_BY_STATUS | null;
  filterCallback: (deal: DealListItem) => boolean;
  enableScope: (scope: string) => void;
  shouldApplyFilteredDealsIds: boolean;
  findInViewFilterCallback: (deal: DealListItem) => boolean;
}) => {
  const columnMapForVirtualization = useMemo(() => {
    const columnMap: Record<
      number,
      {
        statusData:
          | {
              status: Status;
              count: number;
              deals: DealListItem[];
            }
          | undefined;
        deals: DealListItem[];
        count: number;
      }
    > = {};
    let index = 0;

    Statuses.forEach((status) => {
      const statusData = dealsData?.[status];
      const deals =
        statusData?.deals
          .filter(filterCallback)
          .filter(findInViewFilterCallback) || [];
      const count = deals.length;

      if (count == 0) {
        return;
      }

      columnMap[index] = {
        statusData: {
          status,
          count,
          deals,
        },
        deals,
        count,
      };
      index++;
    });

    return columnMap;
  }, [
    dealsData,
    filterCallback,
    findInViewFilterCallback,
    shouldApplyFilteredDealsIds,
  ]);

  const Column = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const { statusData, deals, count } = columnMapForVirtualization[index];
    const status = statusData?.status;

    const Row = ({
      index,
      style,
    }: {
      index: number;
      style: React.CSSProperties;
    }) => {
      const deal = deals[index];
      const hotkeyScope = `${deal.short_id}-deal-line-or-card`;

      return (
        <div
          style={style}
          key={deal.short_id}
          onMouseEnter={() => {
            enableScope(hotkeyScope);
          }}
          onMouseLeave={() => {
            enableScope('');
          }}
          className="overflow-x-hidden"
        >
          <DealCard deal={deal} />
        </div>
      );
    };

    return (
      <div
        key={index}
        className="flex flex-col p-2 rounded-md bg-gray-100"
        style={style}
      >
        <div className="flex flex-row items-center gap-2 text-md  px-2 py-3 text-nowrap">
          {STATUS_OPTIONS_MAP.find((s) => s.value === status)?.icon}

          <div className="text-md">
            {STATUS_OPTIONS_MAP.find((s) => s.value === status)?.label}
          </div>
          <div className="text-sm text-gray-500">{count}</div>
        </div>
        <div className="flex flex-col gap-2 overflow-y-auto h-screen">
          <AutoSizer>
            {({ height, width }) => {
              return (
                <FixedSizeList
                  height={height}
                  width={width}
                  itemCount={deals.length}
                  // 10 for the margin
                  itemSize={110.5 + 10}
                  layout="vertical"
                >
                  {Row}
                </FixedSizeList>
              );
            }}
          </AutoSizer>
        </div>
      </div>
    );
  };

  return (
    <div className="wrapper-statuses h-screen">
      <AutoSizer>
        {({ height, width }) => {
          return (
            <FixedSizeList
              className="List"
              height={height}
              itemCount={Object.keys(columnMapForVirtualization).length}
              itemSize={400}
              width={width}
              layout="horizontal"
            >
              {Column}
            </FixedSizeList>
          );
        }}
      </AutoSizer>
    </div>
  );
};

export default VirtualizedDealBoard;
